import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRight, Clock, Calendar, ShoppingCart, Tag, Package, AlertCircle, LayoutGrid } from 'lucide-react';
import RetailerLayout from '../components/RetailerLayout';
import CmsBlocksRenderer from '../components/CmsBlocksRenderer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getHubConfig } from '../lib/pageConfig';
import useCmsOverlay from '../hooks/useCmsOverlay';
import { useHubNavigation, usePageAccess } from '../hooks/useRetailerPages';

/**
 * HubPage V3 - CMS-ONLY MODE
 * 
 * ARCHITECTURE: 100% CMS-DRIVEN
 * - AUCUN contenu statique (titre, sous-titre, hero)
 * - Navigation est le SEUL élément système (car routing)
 * - Tout le contenu marketing vient du CMS
 * 
 * RÈGLE D'OR:
 * > HUB = CMS blocks uniquement
 * > Si CMS vide → page quasi-vide (navigation seulement)
 * 
 * CONTENU AUTORISÉ (via CMS):
 * ✅ Bannières marketing
 * ✅ Titres/sous-titres
 * ✅ Cartes promotionnelles
 * ✅ Grilles modulaires
 * ✅ Texte éditorial
 * 
 * CONTENU SYSTÈME (non CMS):
 * ✅ Navigation vers sous-pages (routing)
 * ✅ Layout RetailerLayout
 * 
 * INTERDIT:
 * ❌ Titres statiques hardcodés
 * ❌ Sous-titres marketing système
 * ❌ Bannières fallback
 * ❌ Contenu par défaut
 */
const HubPage = () => {
  // Extract slug from current location
  const location = window.location.pathname;
  const extractedSlug = location.split('/dashboard/')[1]?.split('/')[0];
  
  // Récupérer la configuration système du HUB (pour navigation uniquement)
  const hubConfig = getHubConfig(extractedSlug);
  
  // Control Plane: vérifier l'accès et récupérer la navigation filtrée
  const { hasAccess, shouldRedirect, loading: accessLoading } = usePageAccess(extractedSlug);
  const { navigationItems, loading: navLoading } = useHubNavigation(extractedSlug);
  
  // Charger les blocs CMS (SOURCE DE VÉRITÉ pour le contenu)
  const { 
    cmsBlocks, 
    blocksByZone, 
    globalSettings,
    loading: cmsLoading,
    hasCmsOverrides 
  } = useCmsOverlay('hub', extractedSlug, { debug: false });
  
  // Mapping des icônes pour navigation
  const iconMap = {
    Clock,
    Calendar,
    ShoppingCart,
    Tag,
    Package
  };

  // Redirection si la page n'est pas publiée (Control Plane)
  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  // Loading state
  if (accessLoading || cmsLoading) {
    return (
      <RetailerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </RetailerLayout>
    );
  }

  // HUB non trouvé dans la config système
  if (!hubConfig) {
    return (
      <RetailerLayout>
        <div className="flex flex-col items-center justify-center py-20 px-4 min-h-[60vh]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md text-center">
            <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              Page non trouvée
            </h2>
            <p className="text-muted-foreground mb-6">
              La page "{extractedSlug}" n'existe pas.
            </p>
            <Link to="/dashboard">
              <Button className="w-full">Retour au tableau de bord</Button>
            </Link>
          </div>
        </div>
      </RetailerLayout>
    );
  }
  
  // ============================================================================
  // NAVIGATION - SOURCE UNIQUE: CMS NavigationCardsBlock
  // ============================================================================
  // PAGES CMS-ONLY (pas de fallback hardcodé):
  // - nouveautes, precommandes, offres
  // Ces pages utilisent UNIQUEMENT les NavigationCardsBlock du CMS
  // 
  // RÈGLE: Si le CMS a un bloc navigation_cards → utiliser CMS
  //        Sinon → vide (pas de fallback vers hubConfig.navigation)
  // ============================================================================
  const CMS_ONLY_HUBS = ['nouveautes', 'precommandes', 'offres'];
  const isCmsOnlyHub = CMS_ONLY_HUBS.includes(extractedSlug);
  
  // Vérifier si le CMS contient un bloc navigation_cards
  const hasCmsNavigationBlock = cmsBlocks.some(block => block.type === 'navigation_cards');
  
  // Navigation système (UNIQUEMENT si pas CMS-only OU si navigationItems du Control Plane)
  // Pour les HUB CMS-only, la navigation est gérée par NavigationCardsBlock dans CmsBlocksRenderer
  const navigation = isCmsOnlyHub 
    ? [] // CMS-only: pas de navigation système, tout via CMS NavigationCardsBlock
    : (navigationItems.length > 0 
        ? navigationItems 
        : (hubConfig.navigation || []));

  // Vérifier si le CMS a du contenu
  const hasCmsContent = cmsBlocks.length > 0;
  const hasNavigation = navigation.length > 0;
  
  // Pour les HUB CMS-only, considérer hasCmsNavigationBlock comme "has navigation"
  const hasAnyNavigation = hasNavigation || (isCmsOnlyHub && hasCmsNavigationBlock);

  return (
    <RetailerLayout>
      <div className="min-h-[70vh]" data-testid={`hub-page-${extractedSlug}`}>
        
        {/* === ZONE CMS: HEADER (bannières, titres marketing) === */}
        {/* Note: CmsBlocksRenderer gère le layout (max-width, centrage, padding) */}
        {blocksByZone.header.length > 0 && (
          <CmsBlocksRenderer 
            blocks={blocksByZone.header} 
            zone="header" 
            pageType="hub"
          />
        )}
        
        {/* 
          ❌ SUPPRIMÉ: Titre/sous-titre statiques
          RAISON: CMS-ONLY mode - titres doivent venir du CMS
        */}

        {/* === ZONE CMS: CONTENT (contenu principal avant navigation) === */}
        {/* Note: CmsBlocksRenderer gère le layout */}
        {blocksByZone.content.length > 0 && (
          <CmsBlocksRenderer 
            blocks={blocksByZone.content} 
            zone="content" 
            pageType="hub"
          />
        )}

        {/* === NAVIGATION SYSTÈME (routing vers sous-pages) === */}
        {/* UNIFIED: Container global 1200px */}
        {hasNavigation && (
          <section 
            className="hub-navigation-section"
            style={{
              maxWidth: '1200px',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '48px',
              paddingBottom: '48px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <div 
              className="flex flex-wrap justify-center gap-6"
              data-testid="hub-navigation-grid"
            >
              {navigation.map((navItem, index) => {
                const IconComponent = iconMap[navItem.icon] || Package;
                
                return (
                  <Link 
                    key={navItem.id || index}
                    to={navItem.link}
                    className="block group w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-sm"
                    data-testid={`hub-nav-card-${navItem.id}`}
                  >
                    <Card className={`h-full border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${navItem.color || 'bg-primary/5 border-primary/20 hover:border-primary/40'}`}>
                      <CardContent className="p-8 flex flex-col items-center text-center">
                        {/* Icône */}
                        <div className="w-20 h-20 rounded-full bg-background shadow-md flex items-center justify-center mb-6 group-hover:shadow-lg transition-shadow">
                          <IconComponent className="w-10 h-10 text-primary" />
                        </div>
                        
                        {/* Titre */}
                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {navItem.title}
                        </h3>
                        
                        {/* Sous-titre */}
                        {navItem.subtitle && (
                          <p className="text-muted-foreground text-sm mb-6 line-clamp-2">
                            {navItem.subtitle}
                          </p>
                        )}
                        
                        {/* Bouton */}
                        <Button 
                          variant="outline" 
                          className="mt-auto group-hover:bg-primary group-hover:text-white transition-colors"
                        >
                          Découvrir
                          <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* === ZONE CMS: FOOTER (après navigation) === */}
        {/* Note: CmsBlocksRenderer gère le layout */}
        {blocksByZone.footer.length > 0 && (
          <CmsBlocksRenderer 
            blocks={blocksByZone.footer} 
            zone="footer" 
            pageType="hub"
          />
        )}

        {/* === MESSAGE SI PAS DE CMS ET PAS DE NAVIGATION === */}
        {!hasCmsContent && !hasAnyNavigation && (
          <div 
            className="hub-empty-state"
            style={{
              maxWidth: '1200px',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '80px',
              paddingBottom: '80px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <div className="bg-muted/30 rounded-xl p-12 text-center">
              <LayoutGrid className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                Cette page est en cours de configuration.
              </p>
              <p className="text-sm text-muted-foreground/70 mt-2">
                Ajoutez du contenu via l'administration CMS.
              </p>
            </div>
          </div>
        )}
        
        {/* Indicateur CMS (debug) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs">
            CMS: {cmsBlocks.length} | Nav: {navigation.length}
          </div>
        )}
      </div>
    </RetailerLayout>
  );
};

export default HubPage;
